// hooks/usePushAutoSubscribe.js
import { useContext, useEffect, useRef } from 'react';
import { AuthContext } from '../context/AuthContext';
import { usePushNotificationContext } from '../context/PushNotificationContext';

// Registers SW + subscribes automatically after login.
// Doesn't force a permission prompt on first paint — waits for auth.
export function usePushAutoSubscribe() {
  const { isAuthenticated } = useContext(AuthContext);

  const { supported, permission, subscribed, loading, subscriptionStatus, subscribe } = usePushNotificationContext();
  console.log("subscriptionStatus", subscriptionStatus);

  const hasAttempted = useRef(false);

  useEffect(() => {
    // Wait for the initial reconciliation (register SW + check backend
    // status) to finish. Before it resolves, subscriptionStatus is still
    // its default null — indistinguishable from "no backend record" —
    // so deciding to auto-subscribe on this stale value can race ahead
    // of and overwrite a real 'unsubscribed' status the moment it lands.
    if (loading) return;

    if (!isAuthenticated || !supported || hasAttempted.current) return;
    if (subscribed || permission === 'denied') return;

    // Only auto-subscribe for a genuinely new device/browser (status is
    // null — no record at all). An 'unsubscribed' status means the user
    // explicitly opted out here before; respect that instead of silently
    // re-subscribing on every reload.
    if (subscriptionStatus === 'unsubscribed') return;

    hasAttempted.current = true;
    subscribe(); // will trigger the browser's native permission prompt
  }, [loading, isAuthenticated, supported, permission, subscribed, subscriptionStatus, subscribe]);

  // Reset the attempt guard on logout, so a fresh login (possibly by a
  // different user in the same tab) gets its own auto-subscribe attempt
  // instead of being silently skipped because a previous session already
  // used up the ref.
  useEffect(() => {
    if (!isAuthenticated) {
      hasAttempted.current = false;
    }
  }, [isAuthenticated]);
}